'use client';

import type { ExerciseVisualBlock } from '@jeraisonne/ai/visual-blocks';
import { FunctionGraphBlockRenderer } from './function-graph-block';
import { GeometryFigureBlockRenderer } from './geometry-figure-block';
import { VectorDiagramBlockRenderer } from './vector-diagram-block';
import { TableBlockRenderer } from './table-block';
import { ImageVisualBlockRenderer } from './image-visual-block';
import { VideoUploadBlockRenderer } from './video-upload-block';
import { YoutubeEmbedBlockRenderer } from './youtube-embed-block';
import { UnknownVisualBlockRenderer } from './unknown-visual-block';
import { PythonCodeBlockRenderer } from './python-code-block';
import { ScratchCodeBlockRenderer } from './scratch-code-block';
import { ConstrainedGeometryBlockRenderer } from './constrained-geometry-block';
import './figure-paper.css';

/**
 * Types « figure » : rendus sur une feuille de brouillon claire (petits
 * carreaux + marge rouge, `figure-paper.css`, couleurs littérales — pas le
 * token `bg-white` que le thème océan rend translucide), lisible sur tous les
 * thèmes.
 */
const FIGURE_TYPES = new Set<ExerciseVisualBlock['type']>([
  'function_graph',
  'geometry_figure',
  'vector_diagram',
  'constrained_geometry',
]);

function renderBlock(block: ExerciseVisualBlock) {
  switch (block.type) {
    case 'function_graph':
      return <FunctionGraphBlockRenderer block={block} />;
    case 'geometry_figure':
      return <GeometryFigureBlockRenderer block={block} />;
    case 'vector_diagram':
      return <VectorDiagramBlockRenderer block={block} />;
    case 'table':
      return <TableBlockRenderer block={block} />;
    case 'image_visual':
      return <ImageVisualBlockRenderer block={block} />;
    case 'video_upload':
      return <VideoUploadBlockRenderer block={block} />;
    case 'youtube_embed':
      return <YoutubeEmbedBlockRenderer block={block} />;
    case 'unknown_visual':
      return <UnknownVisualBlockRenderer block={block} />;
    case 'python_code':
      return <PythonCodeBlockRenderer block={block} />;
    case 'scratch_code':
      return <ScratchCodeBlockRenderer block={block} />;
    case 'constrained_geometry':
      return <ConstrainedGeometryBlockRenderer block={block} />;
  }
}

export function VisualBlockRenderer({
  block,
  className,
}: {
  block: ExerciseVisualBlock;
  /** Classes additionnelles (ex. `sm:col-span-2` posé par la grille parente). */
  className?: string;
}) {
  return (
    <section
      id={block.id}
      data-visual-block-id={block.id}
      data-figure={FIGURE_TYPES.has(block.type) || undefined}
      className={`border-ink-200 flex h-full flex-col rounded-2xl border bg-white p-2 shadow-sm${
        className ? ` ${className}` : ''
      }`}
    >
      {block.title && <h4 className="text-ink-700 mb-2 text-sm font-semibold">{block.title}</h4>}
      {FIGURE_TYPES.has(block.type) ? (
        <div data-figure-paper className="jr-figure-paper flex flex-1 items-center justify-center">
          {renderBlock(block)}
        </div>
      ) : (
        renderBlock(block)
      )}
      {block.caption && (
        <p className="text-ink-400 mt-2 text-center text-xs italic">{block.caption}</p>
      )}
    </section>
  );
}
